<template>
  <base-modal
    :ref="modalKey"
    :show="open"
    :title="modalTitle"
    :subtitle="internalTable.name"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="80%"
  >
    <v-text-field
      :label="$t('Name')"
      v-model="internalTable.name"
      :error-messages="nameErrors()"
      @input="$v.internalTable.name.$touch()"
      @blur="$v.internalTable.name.$touch()"
    ></v-text-field>

    <v-card outlined>
      <v-card-subtitle>{{ $t("Columns") }}</v-card-subtitle>
      <v-card-text>
        <v-simple-table dense v-if="!loading" class="my-4">
          <template v-slot:default>
            <draggable v-model="internalTable.columns" tag="tbody">
              <tr v-for="(column, index) in internalTable.columns" :key="index">
                <td class="text-center">
                  <v-text-field
                    v-model="column.name"
                    v-bind:label="$t('Name')"
                    :error-messages="columnNameErrors(index)"
                  ></v-text-field>
                </td>
                <td class="text-center">
                  <v-select
                    clearable
                    v-bind:label="$t('LinkedTable')"
                    item-text="text"
                    item-value="id"
                    v-model="column.relatedTableId"
                    :items="tableList"
                  ></v-select>
                </td>
                <td>
                  <v-row justify="center">
                    <v-switch
                      v-model="column.linkableColumn"
                      color="primary"
                      v-bind:label="$t('LinkableColumn')"
                      @change="changeLinkableColumn(index)"
                    />
                  </v-row>
                </td>
                <td>
                  <v-row justify="center">
                    <v-switch
                      v-model="column.isRequired"
                      color="primary"
                      v-bind:label="$t('Required')"
                      @change="changeRequired(index)"
                    />
                  </v-row>
                </td>
                <td class="text-center">
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon @click="remove(index)">
                        <v-icon color="red">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Delete") }}</span>
                  </v-tooltip>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-on="on"
                        icon
                        @click="addColumn"
                        v-show="showAdd(index)"
                      >
                        <v-icon color="success">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Add") }}</span>
                  </v-tooltip>
                </td>
              </tr>
            </draggable>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </base-modal>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { validationMixin } from "vuelidate";
import InternalTableService from "@/services/InternalTableService";
import InternalTable from "@/ecm/models/internalTable/InternalTable";
import InternalTableColumn from "@/ecm/models/internalTable/InternalTableColumn";
import draggable from "vuedraggable";
import Utils from "@/ecm/common/Utils";

export default {
  components: { draggable },
  data() {
    return {
      internalTable: new InternalTable(),
      title: this.$t("InternalTable"),
      modalKey: "internalTableModal",
      saving: false,
      loading: false,
      open: false,
      tableList: [],
    };
  },
  mixins: [validationMixin],
  validations: {
    internalTable: {
      name: { required },
      columns: {
        $each: {
          name: { required },
        },
      },
    },
  },
  computed: {
    modalInstance() {
      return this.$refs[this.modalKey];
    },
    modalTitle() {
      return (
        this.title +
        (this.internalTable.name ? " - " + this.internalTable.name : "")
      );
    },
  },
  methods: {
    getAvailableTables() {
      InternalTableService.getList().then((data) => {
        this.tableList = data;
      });
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.internalTable.name.$dirty) return errors;
      !this.$v.internalTable.name.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    columnNameErrors(index) {
      const errors = [];
      if (!this.$v.internalTable.columns.$each[index].name.$dirty)
        return errors;
      !this.$v.internalTable.columns.$each[index].name.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    onItemSaved() {
      this.gridInstance.refresh();
    },
    saveItem() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }
      if (this.internalTable.id && this.internalTable.id > 0) {
        InternalTableService.validateAffectedFields(this.internalTable.id).then(
          (affectedFields) => {
            if (affectedFields.length > 0) {
              var message = this.$t(
                "DynamicTableEditAssociatedToField"
              ).replaceAll("{0}", affectedFields.join("\n- "));
              this.$confirm(message, {
                title: this.$t("Save"),
                icon: "mdi-alert",
                color: "warning",
                buttonTrueText: this.$t("Save"),
                buttonFalseText: this.$t("Cancel"),
              }).then((res) => {
                if (res == 1) {
                  this.save();
                }
              });
            } else {
              this.save();
            }
          }
        );
      } else {
        this.save();
      }
    },
    save() {
      this.modalInstance.progress(true);
      InternalTableService.save(this.internalTable.toDto())
        .then((result) => {
          if (result) {
            if (this.internalTable.id && this.internalTable.id > 0)
            {
              Utils.toast.success(this.$t("InternalTable") + " " +this.$t("UpdatedSuccessfullyFem"));
            }
            else
            {
              Utils.toast.success(this.$t("InternalTable") + " " +this.$t("CreatedSuccessfullyFem"));
            }
            this.hide();
            this.$emit("on-item-saved");
          }
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.modalInstance.progress(false);
        });
    },
    new() {
      this.getAvailableTables();
      this.internalTable = new InternalTable();
      this.$v.$reset();
      this.show();
    },
    edit(dto) {
      this.getAvailableTables();
      this.internalTable = new InternalTable(dto);
      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
    addColumn() {
      this.internalTable.columns.push(new InternalTableColumn());
    },
    remove(index) {
      this.internalTable.columns.splice(index, 1);
      if (this.internalTable.columns.length == 0) {
        this.addColumn();
      }
    },
    showAdd(index) {
      return index == this.internalTable.columns.length - 1;
    },
    changeLinkableColumn(index) {
      for (let i = 0; i < this.internalTable.columns.length; i++) {
        if (i != index) this.internalTable.columns[i].linkableColumn = false;
      }
      if (this.internalTable.columns[index].linkableColumn) {
        this.internalTable.columns[index].isRequired = true;
      }
    },
    changeRequired(index) {
      if (!this.internalTable.columns[index].isRequired) {
        this.internalTable.columns[index].linkableColumn = false;
      }
    },
  },
};
</script>
